/**
 * AppDownloadPrompt — "Download the app" nudge for iOS-device web users.
 *
 * iOS parity: native iOS users live in the app; web visitors on an iPhone/iPad
 * get a gentle prompt to grab the App Store build.
 *
 * Two surfaces:
 *  - <AppDownloadBanner />: a DISMISSIBLE banner mounted once at the app level
 *    (App.tsx) and shown only on the landing "/", "/login", and "/onboarding"
 *    routes. Dismissal persists in localStorage.
 *  - <AppDownloadSettingsRow />: a small, persistent, low-key mention rendered
 *    inside Settings for logged-in iOS-web users (never a nag, never dismissed).
 *
 * Both render nothing unless the visitor is on an iOS device in a real browser
 * (NOT the in-app webkit bridge, which already IS the app).
 */
import React, { useState } from "react";
import { X } from "lucide-react";
import { colors } from "@/ui";
import { APP_STORE_URL, isIosWebVisitor, isIosSafari } from "@/lib/appDownload";
// The real Parrot app icon (the rounded App Store icon). Imported as a module so
// Vite resolves the hashed URL correctly under the app's "/app" base path.
import APP_ICON from "@/assets/app-icon.png";

const DISMISS_KEY = "appDownloadPromptDismissed";

const SmartAppBannerBody: React.FC = () => (
  <>
    <img
      src={APP_ICON}
      alt=""
      aria-hidden
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        flexShrink: 0,
        objectFit: "cover",
      }}
    />
    <div style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: colors.primary,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        Parrot – Learn Spanish
      </div>
      <div style={{ fontSize: 12, color: colors.grey }}>Spanish Learning App</div>
    </div>
    <span
      style={{
        flexShrink: 0,
        color: colors.blue,
        fontSize: 16,
        fontWeight: 700,
        whiteSpace: "nowrap",
        padding: "6px 8px",
      }}
    >
      Open
    </span>
  </>
);

/**
 * Route-aware dismissible banner. Pass the current pathname; the banner only
 * renders on the landing / login / onboarding routes so it can be mounted once
 * at the app level without editing each page.
 */
export const AppDownloadBanner: React.FC<{ pathname: string }> = ({
  pathname,
}) => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  const onAllowedRoute =
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/onboarding" ||
    pathname.startsWith("/onboarding/");

  // Safari renders the NATIVE Smart App Banner (apple-itunes-app meta tag) with
  // real install-detection, so stand down there; this custom banner is only for
  // the non-Safari iOS browsers that don't get the native one.
  if (dismissed || !onAllowedRoute || !isIosWebVisitor() || isIosSafari())
    return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* best-effort; banner still hides for this session */
    }
    setDismissed(true);
  };

  // Mirrors the iOS native Smart App Banner (which Safari shows automatically;
  // this is the fallback for non-Safari iOS). The whole banner → App Store, the
  // blue "Open" → App Store (we can't detect install or deep-link the app from a
  // non-Safari iOS browser — only the native banner can), and the gray × in a
  // circle dismisses. No Play Store listing exists, so this is iOS-only.
  return (
    <div
      role="region"
      aria-label="Get the Parrot app"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        paddingTop: "max(8px, env(safe-area-inset-top))",
        background: colors.white,
        borderBottom: `1px solid ${colors.greyLightest}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 12,
          textDecoration: "none",
        }}
      >
        <SmartAppBannerBody />
      </a>

      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        style={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: colors.greyLightest,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.grey,
          padding: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};

/**
 * Small, persistent, low-key row for Settings (logged-in iOS-web users).
 * Renders nothing on non-iOS so it never looks broken or out of place.
 */
export const AppDownloadSettingsRow: React.FC = () => {
  if (!isIosWebVisitor()) return null;

  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        borderRadius: 16,
        background: colors.white,
        border: `1px solid ${colors.greyLightest}`,
        textDecoration: "none",
      }}
    >
      <SmartAppBannerBody />
    </a>
  );
};
